import React, { useMemo } from 'react';
import { backofficeOrganisationApi } from 'api/backoffice/organisation';
import { backofficeParkingApi } from 'api/backoffice/parking';
import { backofficePermitApi } from 'api/backoffice/permit';
import { useAuth } from 'context/authContext';
import { PermissionProvider } from 'context/permissionContext';
import { FeatureProvider } from 'context/featureContext';
import { backofficeCameraSettings } from 'api/backoffice/cameraSettings';
import { backofficeUserApi } from 'api/backoffice/user';
import { backofficeDropdownOptionsAPI } from 'api/backoffice/dropdownOptions';
import { backofficeFineAPI } from 'api/backoffice/fine';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { languageObject } from 'constants/lang';

const FIVE_MIN = 5 * 60 * 1000;
const TEN_MIN = 10 * 60 * 1000;

const ApiContext = React.createContext({});
export const useApi = () =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    React.useContext(ApiContext) as Record<string, any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
const ApiProvider = (props: any): JSX.Element => {
    const authContext = useAuth();
    const perms = authContext.userPermissions;
    const enabled = authContext.isAuthenticated;
    const isBackoffice = authContext.backoffice_organisation === true;
    const { i18n, t } = useTranslation();

    const { data: ParkingLots = [] } = useQuery({
        queryKey: ['parkingLots'],
        queryFn: async () => {
            const res = await backofficeParkingApi.filterGetListOfParkingLots();
            return res.payload.data ?? [];
        },
        enabled,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const { data: Organisations = [] } = useQuery({
        queryKey: ['organisations'],
        queryFn: async () => {
            const res =
                await backofficeOrganisationApi.filterGetListOfOrganisations();
            return res.payload.data ?? [];
        },
        enabled,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const { data: Users = [] } = useQuery({
        queryKey: ['usersDropdown'],
        queryFn: async () => {
            const res = await backofficeUserApi.userListDropdown({
                show_active: true,
            });
            return res.data ?? [];
        },
        enabled,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const { data: Cameras = [] } = useQuery({
        queryKey: ['cameraSettings'],
        queryFn: async () => {
            const res = await backofficeCameraSettings.getAllCameraSettings({
                limit: 1000,
                offset: 0,
                parkinglot_ids: '',
                search_term: '',
                sortcol: 'address_name',
                sortorder: 'ASC',
            });
            return res.payload?.data ?? [];
        },
        enabled: enabled && !!perms?.['Camera settings']?.canRead,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const { data: rawPermitTypes = [] } = useQuery({
        queryKey: ['permitTypes'],
        queryFn: async () => {
            const res = await backofficePermitApi.getPermitTypes();
            return res.payload ?? [];
        },
        enabled: enabled && !!perms?.Permits?.canRead,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const { data: rawParkingStatuses = [] } = useQuery({
        queryKey: ['parkingStatusOptions'],
        queryFn: async () => {
            const res =
                await backofficeDropdownOptionsAPI.getParkingStatusDropdown();
            return res.payload?.items ?? [];
        },
        enabled:
            enabled && (!!perms?.Fines?.canRead || !!perms?.Permits?.canRead),
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const langCode =
        languageObject.find((l) => l.id === i18n.language)?.langCode ?? 'da';

    const FineStatusOptions = useMemo(() => {
        const statusMap = new Map(rawParkingStatuses.map((s) => [s.id, s]));
        return rawParkingStatuses
            .filter(
                (item) =>
                    item.applies_to === 'fine' || item.applies_to === 'both'
            )
            .map((item) => {
                if (isBackoffice) {
                    return {
                        id: item.id,
                        alias_of: null,
                        name:
                            item.translations?.[langCode]?.name ||
                            item.translations?.da?.name ||
                            item.translations?.en?.name ||
                            '',
                    };
                }
                const parent =
                    item.alias_of != null ? statusMap.get(item.alias_of) : null;
                return {
                    id: item.id,
                    alias_of: item.alias_of ?? null,
                    name: parent
                        ? parent.translations?.[langCode]?.name ||
                          parent.translations?.da?.name ||
                          parent.translations?.en?.name ||
                          ''
                        : item.translations?.[langCode]?.name ||
                          item.translations?.da?.name ||
                          item.translations?.en?.name ||
                          '',
                };
            });
    }, [rawParkingStatuses, langCode, isBackoffice]);

    const PermitTypes = useMemo(() => {
        return rawPermitTypes.map((item) => ({
            ...item,
            name: t(item.name),
        }));
    }, [rawPermitTypes, t]);

    const PermitStatusOptions = useMemo(() => {
        return rawParkingStatuses
            .filter(
                (item) =>
                    item.applies_to === 'permit' || item.applies_to === 'both'
            )
            .map((item) => ({
                id: item.id,
                name:
                    item.translations?.[langCode]?.name ||
                    item.translations?.da?.name ||
                    item.translations?.en?.name ||
                    '',
            }));
    }, [rawParkingStatuses, langCode]);

    const { data: Offenses = [] } = useQuery({
        queryKey: ['offenses'],
        queryFn: async () => {
            const res = await backofficeFineAPI.getOffense();
            if (res.error === null && res.statusCode === 200) {
                return res.payload ?? [];
            }
            return [];
        },
        enabled: enabled && !!perms?.Fines?.canRead,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const { data: CarBrands = [] } = useQuery({
        queryKey: ['carBrands'],
        queryFn: async () => {
            const res = await backofficeFineAPI.getCarBrands();
            if (res.error === null && res.statusCode === 200) {
                return res.payload ?? [];
            }
            return [];
        },
        enabled: enabled && !!perms?.Fines?.canRead,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });
    const { data: FineInterruptions = [] } = useQuery({
        queryKey: ['fineInterruption'],
        queryFn: async () => {
            const res = await backofficeFineAPI.getFineClearReasons({
                limit: 1000,
                offset: 0,
                searchTerm: '',
                sortColumn: 'event_comment',
                sortOrder: 'ASC',
            });
            if (res.error === null && res.statusCode === 200) {
                return res.payload.data ?? [];
            }
            return [];
        },
        enabled: enabled && !!perms?.Fines?.canRead,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const { data: rawDeliveryMethods = [] } = useQuery({
        queryKey: ['deliveryMethods'],
        queryFn: async () => {
            const res =
                await backofficeDropdownOptionsAPI.getDeliveryMethodDropdown();
            return res.payload?.items ?? [];
        },
        enabled,
        staleTime: FIVE_MIN,
        gcTime: TEN_MIN,
    });

    const DeliveryMethods = useMemo(() => {
        return rawDeliveryMethods.map((item) => ({
            id: item.id,
            type: item.type,
            name:
                item.translations?.[langCode]?.name ||
                item.translations?.da?.name ||
                item.translations?.en?.name ||
                '',
        }));
    }, [rawDeliveryMethods, langCode]);

    return (
        <FeatureProvider>
            <PermissionProvider>
                <ApiContext.Provider
                    value={{
                        ParkingLots,
                        Organisations,
                        Users,
                        FineStatusOptions,
                        PermitStatusOptions,
                        PermitTypes,
                        Cameras,
                        Offense: Offenses,
                        CarBrands,
                        FineInterruptions,
                        DeliveryMethods,
                    }}
                    {...props}
                />
            </PermissionProvider>
        </FeatureProvider>
    );
};

export default ApiProvider;
